import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, useTheme } from '@mui/material';
import TextInput from '../../theme/inputs/TextInput';
import Text from '../../theme/text/Text';
import { useTranslation } from "react-i18next";
import { useUpdateSettingForRelationMutation, useUpdateSettingMutation } from '../../../newapi/global/generalApiSlice';
import { tokens } from '../../../theme';
import EMGeneralExplainerBox from '../ModalsPopover/explainers/EMGeneralExplainerBox';

// variants
//    1: default has both a title and subtitle
//    2: has no subtitle ans text is light
//    3: like default but raised dark background (one grey step up) for darker surfaces
//
// manualSave: replaces the debounced autosave with a report through
//   `onChange(value, setting)`, for a screen that saves several settings behind one Save
//   button of its own, as useManualSettingsSave does.
const StringSetting = (props) => {
  const { title = true, subtitle = true, variant = 1, manualSave = false} = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const raisedDarkBg = variant === 3 ? colors.grey[400] : colors.grey[300];
  const [updateSetting] = useUpdateSettingMutation();
  const [updateSettingForRelation] = useUpdateSettingForRelationMutation();

  const [setting, setSetting] = useState();
  const [value, setValue] = useState('');
  const debounceRef = useRef(null);

  const debouncedUpdateSetting = useCallback(async (settingData, newValue) => {
    try {
      if(props.relationId){
        await updateSettingForRelation({
          key: settingData.key,
          value: newValue,
          relationId: props.relationId
        });
      } else {
        await updateSetting({
          key: settingData.key,
          value: newValue,
          warehouseId: settingData.warehouseId,
        });
      }
    } catch (error) {
      console.error('Failed to update setting:', error);
    }
  }, [updateSetting, updateSettingForRelation, props.relationId]);

  const handleChange = useCallback((event) => {
    const newValue = event.target.value;
    setValue(newValue);

    if (props.onChange) {
      props.onChange(newValue, props.relationId ? { ...setting, relationId: props.relationId } : setting);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Manual-save fields are persisted by the screen they sit on.
    if (manualSave) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      if (setting) {
        debouncedUpdateSetting(setting, newValue);
      }
    }, 500);
  }, [setting, debouncedUpdateSetting, props.onChange, props.relationId, manualSave]);

  useEffect(() => {
    if(props.skey && typeof props.settings !== "undefined"){
        let xsetting = props.settings.find((setting) => setting.key === props.skey);
        if (!xsetting) {
            xsetting = { key: props.skey, value: '', warehouseId: -1 };
        }
        setSetting(xsetting);
        setValue(xsetting.value || '');
    }
  }, [props.settings, props.skey]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        width: 1,
      }}
    >
      <Box
        sx={{
          display:"none",
          "@media screen and (max-width: 47.9375em)": {
            display:"block"
          },
        }}
      >
        <Box
          sx={{
            ml:"-12px",
            mr:"-12px",
            pl:2,
            pr:1,
            display:"flex",
            py:1,
            borderRadius:7,
            gap:2,
            alignItems:"center",
            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
          }}
        >
          <Text
            sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
            noBreak 
            semibold
          >
              {t(`${setting?.key}_title`)}
              {props.explainer && <EMGeneralExplainerBox title={props.explainerTitle} text={props.explainerText}/>}
          </Text>
          

          <Box
            sx={{
              flex: 1,
              maxWidth: props.maxWidth ?? 300,
              marginLeft: "auto",
              backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
              borderRadius: 4,
              display: "flex",
              height: 30,

              
            }}
          >
            <TextInput
              height={30}
              value={value}
              onChange={handleChange}
              placeholder={props.placeholder}
              sx={{
                flex:1,
                inputSX:props.inputSX
              }}
            />
          </Box>

        </Box>
        {subtitle && variant !== 2 && 
          <Text 
            light
            sx={{
              pt:1,
            }}
          >
            {t(`${setting?.key}_subtitle`)}
          </Text>
        }

      </Box>
      <Box
        sx={{
          width: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
          "@media screen and (max-width: 47.9375em)": {
            display:"none"
          },
        }}
      >
        <Box
          sx={{
            display:"flex",
            flexDirection:"column",
          }}
        >
          {title && <Text  size={variant === 2 ?? "fs13"} semibold={props.variant !== 2} >{t(`${setting?.key}_title`)}</Text>}
          {subtitle && variant !== 2 && <Text light>{t(`${setting?.key}_subtitle`)}</Text>}
        </Box>

        <Box
          sx={{
            flex: 1,
            maxWidth: props.maxWidth ?? 300,
            marginLeft: "auto",
            backgroundColor: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
            borderRadius: 4,
            display: "flex",
            height: 30,
          }}
        >
          <TextInput
            height={30}
            value={value}
            onChange={handleChange}
            placeholder={props.placeholder}
            sx={{
              flex:1,
              inputSX:props.inputSX
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default StringSetting;